import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalIcon, MapPin } from "lucide-react";

/**
 * StickyTripBar — always-visible slim summary of dates + destination once
 * a trip has been set AND the user scrolled past the TripSetup section.
 * Hides again once the booking section has fully scrolled off (so it
 * doesn't nag on Delivery / Testimonials / FAQ / Footer).
 */
export const StickyTripBar = ({
  range, isOffProperty, zoneId, resort, address, deliverySlot, pickupSlot,
  zones, timeSlots, onEditTrip,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const tripEl = document.getElementById("trip-setup");
      const bookEl = document.getElementById("book");
      if (!tripEl) { setVisible(false); return; }
      const tripRect = tripEl.getBoundingClientRect();
      const bookPassed = bookEl ? bookEl.getBoundingClientRect().bottom < 60 : false;
      setVisible(tripRect.bottom < 60 && !bookPassed);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isSet = !!(range?.from && range?.to && deliverySlot);
  if (!isSet || !visible) return null;

  const destLabel = (() => {
    if (isOffProperty) {
      const z = zones.find((zz) => zz.id === zoneId);
      if (z?.requires_address) return address || z?.area || "";
      return resort || z?.area || "";
    }
    return resort;
  })();
  const timeLabel = (id) => (timeSlots || []).find((s) => s.id === id)?.label?.replace(" – ", "–") || id;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 top-20 md:top-24 w-[min(96vw,900px)]"
      data-testid="sticky-trip-bar"
    >
      <div className="rounded-full border border-emerald-500/45 bg-[#0A2438]/95 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.55)] pl-4 pr-2 py-2 flex items-center gap-3 text-[#F6F1E4] text-xs">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-emerald-300 font-medium">
          <CalIcon className="w-3.5 h-3.5" />
          {format(range.from, "MMM d")} → {format(range.to, "MMM d")}
        </span>
        <span className="sm:hidden inline-flex items-center gap-1.5 text-emerald-300 font-medium">
          <CalIcon className="w-3.5 h-3.5" />
          {format(range.from, "M/d")}–{format(range.to, "M/d")}
        </span>
        <span className="hidden md:inline text-[10px] text-[#B9C8D5]">{timeLabel(deliverySlot)}</span>
        <span className="text-[#FFD166]">·</span>
        <span className="inline-flex items-center gap-1.5 truncate flex-1 min-w-0">
          <MapPin className="w-3.5 h-3.5 text-[#FFD166] shrink-0" />
          <span className="truncate" data-testid="sticky-trip-bar-destination">{destLabel}</span>
        </span>
        <button
          type="button"
          onClick={onEditTrip}
          className="shrink-0 text-[11px] font-medium px-3 py-1.5 rounded-full bg-[#FFD166] text-[#0A2438] hover:brightness-110 transition-all"
          data-testid="sticky-trip-bar-edit"
        >
          Change
        </button>
      </div>
    </div>
  );
};

export default StickyTripBar;
